import {
	AdAccountDTO,
	FacebookAdsCampaignDTO,
	GoogleAdsCampaignDTO
} from 'api-models'
import {
	ChangeEvent,
	createContext,
	Dispatch,
	ReactNode,
	SetStateAction,
	useEffect,
	useState
} from 'react'
import { useSearchParams } from 'react-router-dom'

export type HandleSubItemChangeProps = {
	event: ChangeEvent<HTMLInputElement>
	key: string
	value: string | string[]
}

export type OptionSubContentProps = {
	handleSubItemChange: ({ event, key, value }: HandleSubItemChangeProps) => void
	filtersMap: Map<any, any>
}

export type FiltersType = {
	[key: string]: string | string[] | undefined
	type?: string
	status?: string[]
	entity?: string
	scope?: string
	campaigns?: string[]
	block_id?: string
}

interface ExclusionsPageFiltersContextType {
	filters: FiltersType
	filtersMap: Map<any, any>
	selectedAdAccount: AdAccountDTO | null
	setSelectedAdAccount: Dispatch<SetStateAction<AdAccountDTO | null>>
	selectedCampaign: GoogleAdsCampaignDTO | FacebookAdsCampaignDTO | null
	setSelectedCampaign: Dispatch<
		SetStateAction<GoogleAdsCampaignDTO | FacebookAdsCampaignDTO | null>
	>
	handleApplyFilters: (newFilters: FiltersType) => void
	handleApplyDetailsItemFilter: (newFilters: FiltersType) => void
	handleSubItemChange: ({ event, key, value }: HandleSubItemChangeProps) => void
	handleRemoveFilter: (filterKeyToRemove: string) => void
	handleOpenExclusionDetailsChange: (open: boolean) => void
}
export const ExclusionsPageFiltersContext = createContext(
	{} as ExclusionsPageFiltersContextType
)

const FILTER_KEYS_TYPE_ARRAY = ['status', 'campaigns']

// Valid filter keys for exclusions page
const VALID_FILTER_KEYS = [
	'type',
	'status',
	'entity',
	'scope',
	'campaigns',
	'adAccount',
	'campaign',
	'breadCampaign'
]

const NAVIGATION_KEYS = ['adAccount', 'campaign', 'breadCampaign']

// Filter keys applied to the API query that don't originate from the filters
// dropdown. Used when arriving from a "View exclusions" link that scopes by
// blacklist block id (DEV-2348). Shown as removable chips so the user can see
// the scope is active and clear it; otherwise the param persists silently
// across navigation and the list looks unfiltered when it isn't.
const EXTERNAL_FILTER_KEYS = ['block_id']

interface ExclusionsPageFiltersContextProviderProps {
	children: ReactNode
}
export function ExclusionsPageFiltersContextProvider({
	children
}: ExclusionsPageFiltersContextProviderProps) {
	const [searchParams, setSearchParams] = useSearchParams()

	const [filters, setFilters] = useState({} as FiltersType)
	const [filtersMap, setMapState] = useState(new Map())
	const [selectedAdAccount, setSelectedAdAccount] =
		useState<AdAccountDTO | null>(null)
	const [selectedCampaign, setSelectedCampaign] = useState<
		GoogleAdsCampaignDTO | FacebookAdsCampaignDTO | null
	>(null)

	useEffect(() => {
		const filters = {}
		const map = new Map()

		searchParams.forEach((value, key) => {
			if (NAVIGATION_KEYS.includes(key)) return
			if (
				VALID_FILTER_KEYS.includes(key) ||
				EXTERNAL_FILTER_KEYS.includes(key)
			) {
				const formattedValue = FILTER_KEYS_TYPE_ARRAY.includes(key)
					? value.split(',')
					: value

				Object.assign(filters, { [key]: formattedValue })
				map.set(key, formattedValue)
			}
		})

		setFilters(filters)
		setMapState(map)
	}, [searchParams])

	const handleApplyFilters = (newFilters: FiltersType) => {
		setFilters(newFilters)
		// Preserve marketing params and other non-filter params
		const currentParams = new URLSearchParams(searchParams)

		// Only update valid filter keys
		Object.keys(newFilters).forEach((key) => {
			if (VALID_FILTER_KEYS.includes(key)) {
				const value = newFilters[key]
				if (value) {
					currentParams.set(key, Array.isArray(value) ? value.join(',') : value)
				} else {
					currentParams.delete(key)
				}
			}
		})

		setSearchParams(currentParams)
	}

	const handleApplyDetailsItemFilter = (newFilters: FiltersType) => {
		// Preserve marketing params and other non-filter params
		const currentParams = new URLSearchParams(searchParams)

		// Remove only filter keys, preserve marketing params
		for (const key of currentParams.keys()) {
			if (VALID_FILTER_KEYS.includes(key)) {
				currentParams.delete(key)
			}
		}

		const map = new Map()
		Object.keys(newFilters).forEach((key) => {
			if (VALID_FILTER_KEYS.includes(key)) {
				const value = newFilters[key] as string
				if (value) {
					map.set(key, value)
					currentParams.set(key, value)
				}
			}
		})

		setSearchParams(currentParams)
		setMapState(map)
		setFilters(newFilters)
	}

	const handleSubItemChange = ({
		event,
		key,
		value
	}: HandleSubItemChangeProps) => {
		event.stopPropagation()
		if (value.length === 0) {
			handleRemoveFilter(key)
			return
		}
		setMapState((map) => new Map(map.set(key, value)))
	}

	const handleRemoveFilter = (filterKeyToRemove: string) => {
		const copy = filtersMap
		copy.delete(filterKeyToRemove)
		setMapState(new Map(copy))

		const filters = mapFilters(copy)
		setFilters(filters)

		// Delete from the URL for both dropdown filters and external scope params
		if (
			VALID_FILTER_KEYS.includes(filterKeyToRemove) ||
			EXTERNAL_FILTER_KEYS.includes(filterKeyToRemove)
		) {
			const currentParams = new URLSearchParams(searchParams)
			currentParams.delete(filterKeyToRemove)
			setSearchParams(currentParams)
		}
	}

	const handleOpenExclusionDetailsChange = (open: boolean) => {
		if (!open) {
			const filters = mapFilters(filtersMap)
			handleApplyFilters(filters)
		}
	}

	const mapFilters = (map: Map<any, any>) => {
		const filters = {}
		map.forEach((value, key) => Object.assign(filters, { [key]: value }))
		return filters as FiltersType
	}

	return (
		<ExclusionsPageFiltersContext.Provider
			value={{
				filters,
				filtersMap,
				selectedAdAccount,
				setSelectedAdAccount,
				selectedCampaign,
				setSelectedCampaign,
				handleApplyFilters,
				handleRemoveFilter,
				handleApplyDetailsItemFilter,
				handleOpenExclusionDetailsChange,
				handleSubItemChange
			}}
		>
			{children}
		</ExclusionsPageFiltersContext.Provider>
	)
}
